import { Box, useColorModeValue, useDisclosure } from "@chakra-ui/react";
import { Bundle, BundleEntry, Resource } from "@medplum/fhirtypes";
import { useEffect, useState } from "react";
import { EhrName } from "../../../domain/ehr";
import { DemographicsModal } from "../shared-components/demos-modal";
import { PatientFHIRData } from "../shared-components/fhir-sections";
import { SummaryContent } from "../shared-components/fhir-sections/summaries/content";
import { PatientHeader } from "../shared-components/header";
import { MrModal } from "../shared-components/mr-modal";
import { PatientForm } from "../shared-components/patient-form";
import { PatientMatches } from "../shared-components/patient-matches";
import { DocumentProgressContainer } from "../shared-components/progress-bar";
import { RawDocuments } from "../shared-components/raw-documents";
import { PatientSearch } from "../shared-components/search";
import { PatientTabs } from "../shared-components/tabs";
import { usePatientConsolidatedContext } from "../shared-logic/consolidated-context";
import {
  PatientConsolidatedStateActionType,
  PatientQueryParamProps,
  PatientTab,
  getValidPatientTab,
} from "../shared-logic/consolidated-context/reducer";
import { useSharedContext } from "../shared-logic/shared-context";
import { useGetConsolidated } from "../shared-logic/useGetConsolidated";
import { useGetEhrDiff } from "../shared-logic/useGetEhrDiff";
import { useGetFacilities } from "../shared-logic/useGetFacilties";
import { useGetFilters } from "../shared-logic/useGetFilters";
import { useGetMedicalRecord } from "../shared-logic/useGetMedicalRecord";
import { useGetPatientInfo } from "../shared-logic/useGetPatientInfo";
import { useGetPatientMatches } from "../shared-logic/useGetPatientMatches";
import { usePatientHieOptOut } from "../shared-logic/usePatientHieOptOut";
import { useQueryDocuments } from "../shared-logic/useQueryDocuments";
import { useSearch } from "../shared-logic/useSearch";
import { useSubmitPatient } from "../shared-logic/useSubmitPatient";

export type SectionCounts = {
  sectionCounts: {
    reports: number;
    documents: number;
  };
};

export function PatientOverview({
  patientId,
  patientQueryParams,
  isImpersonating,
}: {
  patientId: string;
  patientQueryParams?: PatientQueryParamProps | undefined;
  isImpersonating?: boolean;
}) {
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [usingMetriportOnlyBundleFromConsolidated, setUsingMetriportOnlyBundleFromConsolidated] =
    useState<boolean>(false);
  const [metriportOnlyBundleFromConsolidated, setMetriportOnlyBundleFromConsolidated] = useState<
    Bundle<Resource> | undefined
  >(undefined);
  const [sidePanelContent, setSidePanelContent] = useState<React.ReactNode | undefined>(undefined);

  const cxDidEnableSectionSearch = localStorage.getItem("usingSectionSearch") === "true";
  const [usingSectionSearch, setUsingSectionSearch] = useState<boolean>(cxDidEnableSectionSearch);

  const patientFormDisclosure = useDisclosure();
  const patientMRDisclosure = useDisclosure();
  const patientDemosDisclosure = useDisclosure();
  const patientMatchesDisclosure = useDisclosure();

  const [patientTab, setPatientTab] = useState<PatientTab>(
    getValidPatientTab(patientQueryParams?.patientTab)
  );

  const { sharedState } = useSharedContext();
  const isEhr = !!sharedState.ehrDetails;
  const isAthena = isEhr && sharedState.ehrDetails?.name === EhrName.Athena;
  const { patientConsolidatedDispatch, patientConsolidatedState } = usePatientConsolidatedContext();
  const { createOrUpdatePatient, isSubmitting } = useSubmitPatient({
    patientId,
    onComplete: () => patientFormDisclosure.onClose(),
  });
  const { patient, isFetchingPatient, setPatient } = useGetPatientInfo({ patientId });
  const { filters } = useGetFilters();
  const { facilities } = useGetFacilities({ isDisabledOnLoad: isEhr });
  const { patientMatches } = useGetPatientMatches({ patientId, isDisabledOnLoad: isEhr });
  const { patientHieOptOut, toggleHieOptOut } = usePatientHieOptOut({
    patientId,
    isDisabledOnLoad: isEhr,
  });

  const documentsState = patientConsolidatedState[patientId]?.documents;
  const lastDocQueryState = patientConsolidatedState[patientId]?.lastDocQuery;
  const initialDqRunState = patientConsolidatedState[patientId]?.initialDqRun;
  const consolidatedState = patientConsolidatedState[patientId]?.consolidated;
  const searchResultsState = patientConsolidatedState[patientId]?.searchResults;

  const { existingMrStatus, isMrDownloading, isAtLeastOneMrDownloading, onDownloadMR } =
    useGetMedicalRecord({ patientId });

  const {
    isQueryingDocuments,
    isListingDocuments,
    isQueryError,
    dqProgress,
    documentListResult,
    initialDocumentListResult,
    lastDocQuery,
    searchDocFilters,
    onQueryPatientDocuments,
    onSearchPatientDocuments,
  } = useQueryDocuments({ patientId });

  const { isQueryingConsolidated, consolidated, onQueryPatientConsolidated } = useGetConsolidated({
    patientId,
  });

  const { searchResults, onSearch, isSearchingResults } = useSearch({
    patientId,
  });

  const {
    isFetchingDiff,
    metriportOnlyBundle,
    metriportOnlyResourceTypes,
    getMetriportOnlyBundleJob,
  } = useGetEhrDiff({
    ehr: sharedState.ehrDetails?.name,
    ehrPatientId: sharedState.ehrDetails?.ehrPatientId,
    diff: sharedState.ehrDetails?.diff,
  });

  useEffect(() => {
    if (!documentsState && searchDocFilters) {
      onSearchPatientDocuments({ ...searchDocFilters, setFiltersInStorage: false });
    }
  }, [documentsState, searchDocFilters]);

  useEffect(() => {
    async function initDq() {
      if (!initialDqRunState && initialDocumentListResult) {
        if (initialDocumentListResult.documents.length === 0) await onQueryPatientDocuments();
        patientConsolidatedDispatch({
          type: PatientConsolidatedStateActionType.setPatientsInitialDqRun,
          patientId,
          initialDqRun: "initialized",
        });
      }
    }
    initDq();
  }, [initialDqRunState, initialDocumentListResult]);

  useEffect(() => {
    if (
      consolidatedState === undefined &&
      initialDqRunState &&
      !isQueryingDocuments &&
      !isQueryError
    ) {
      onQueryPatientConsolidated();
    }
  }, [consolidatedState, initialDqRunState, isQueryingDocuments, isQueryError]);

  useEffect(() => {
    if (documentListResult) {
      patientConsolidatedDispatch({
        type: PatientConsolidatedStateActionType.setPatientsDocuments,
        patientId,
        documents: documentListResult.documents,
      });
    }
  }, [documentListResult]);

  useEffect(() => {
    if (lastDocQuery) {
      patientConsolidatedDispatch({
        type: PatientConsolidatedStateActionType.setPatientsLastQueryAt,
        patientId,
        lastDocQuery,
      });
    }
  }, [lastDocQuery]);

  useEffect(() => {
    if (consolidated) {
      patientConsolidatedDispatch({
        type: PatientConsolidatedStateActionType.setPatientsConsolidated,
        patientId,
        consolidatedBundle: consolidated,
      });
      getMetriportOnlyBundleJob();
    }
  }, [consolidated]);

  useEffect(() => {
    if (
      consolidated &&
      consolidated.entry &&
      consolidated.entry.length > 0 &&
      metriportOnlyBundle &&
      metriportOnlyBundle.entry &&
      metriportOnlyBundle.entry.length > 0 &&
      metriportOnlyResourceTypes
    ) {
      const bundle = searchResults ?? consolidated;
      const entries = bundle.entry ?? [];

      const metriportOnlyBundleResourceIds = metriportOnlyBundle.entry.flatMap(e => {
        return e.resource?.id ? [e.resource.id] : [];
      });
      const metriportOnlyResourcesFromConsolidated: BundleEntry<Resource>[] = [];
      entries.map(entry => {
        const metriportOnly = entry.resource?.id
          ? metriportOnlyBundleResourceIds.includes(entry.resource.id) ||
            !metriportOnlyResourceTypes.includes(entry.resource.resourceType)
          : true;
        if (metriportOnly) metriportOnlyResourcesFromConsolidated.push(entry);
      });
      setMetriportOnlyBundleFromConsolidated({
        ...consolidated,
        entry: metriportOnlyResourcesFromConsolidated,
      });
    }
  }, [consolidated, metriportOnlyBundle, searchResults]);

  useEffect(() => {
    const usedConsolidated = usingMetriportOnlyBundleFromConsolidated
      ? metriportOnlyBundleFromConsolidated
      : consolidated;
    if (!usedConsolidated) return;
    patientConsolidatedDispatch({
      type: PatientConsolidatedStateActionType.setPatientsConsolidated,
      patientId,
      consolidatedBundle: usedConsolidated,
    });
  }, [usingMetriportOnlyBundleFromConsolidated, consolidated, metriportOnlyBundleFromConsolidated]);

  function toggleUsingSectionSearch() {
    const newSectionSearchState = !usingSectionSearch;
    setUsingSectionSearch(newSectionSearchState);
    localStorage.setItem("usingSectionSearch", newSectionSearchState.toString());
  }

  function setPatientTabAndParams(patientTab: PatientTab) {
    setPatientTab(patientTab);
    patientQueryParams?.setPatientQueryParams({ patientTab });
  }

  const isStickyHeader = isAthena;
  const isLimited = isEhr;
  const isImpersonatingOffset = isImpersonating ? 45 : 0;

  function onUserSearch(query: string) {
    patientQueryParams?.clearPatientQueryParams();
    setSidePanelContent(undefined);
    onSearch(query);
  }

  function onUserClearSearch() {
    patientQueryParams?.clearPatientQueryParams();
    setSidePanelContent(undefined);
    patientConsolidatedDispatch({
      type: PatientConsolidatedStateActionType.clearPatientsSearchResults,
      patientId,
    });
  }

  return (
    <>
      {/* V1 Going to add Breadcrumbs */}
      <Box
        p={5}
        maxW={"2560px"}
        minH={"100vh"}
        mx={"auto"}
        bg={useColorModeValue("gray.100", "gray.900")}
      >
        <Box
          bg={useColorModeValue("gray.100", "gray.900")}
          position={isStickyHeader ? "sticky" : "relative"}
          top={0}
          zIndex={4}
        >
          <PatientHeader
            patient={patient}
            isFetchingPatient={isFetchingPatient}
            isQueryingDocuments={isQueryingDocuments || isQueryingConsolidated}
            isAtLeastOneMrDownloading={isAtLeastOneMrDownloading}
            isHieOptedOut={patientHieOptOut}
            usingSectionSearch={usingSectionSearch}
            lastDocQuery={lastDocQueryState}
            actions={{
              onQueryPatientDocuments: async () => {
                await onQueryPatientDocuments();
                patientConsolidatedDispatch({
                  type: PatientConsolidatedStateActionType.resetPatientsConsolidated,
                  patientId,
                });
              },
              onGenerateMedicalRecord: patientMRDisclosure.onOpen,
              onEditPatient: patientFormDisclosure.onOpen,
              onDisplayPatientDemos: patientDemosDisclosure.onOpen,
              onViewPatientMatches: patientMatchesDisclosure.onOpen,
              onSetPatientTab: setPatientTabAndParams,
              toggleUsingSectionSearch,
              toggleHieOptOut,
              setUsingMetriportOnlyBundleFromConsolidated:
                metriportOnlyBundleFromConsolidated &&
                metriportOnlyBundleFromConsolidated.entry &&
                metriportOnlyBundleFromConsolidated.entry.length > 0
                  ? setUsingMetriportOnlyBundleFromConsolidated
                  : undefined,
            }}
            isFetchingDiff={isFetchingDiff}
            usingMetriportOnlyBundleFromConsolidated={usingMetriportOnlyBundleFromConsolidated}
            isLimited={isLimited}
            setHeaderHeight={setHeaderHeight}
            selectedTab={patientTab}
          />
          {patientTab === "fhir" && isStickyHeader && (
            <>
              <PatientSearch
                onSearch={onUserSearch}
                onClear={onUserClearSearch}
                isLoading={isQueryingDocuments || isQueryingConsolidated}
              />
              <PatientTabs
                filters={filters}
                mappedConsolidated={searchResultsState ?? consolidatedState}
                isIFrame={isEhr}
                isLimited={isLimited}
                headerHeight={headerHeight + isImpersonatingOffset}
              />
            </>
          )}
        </Box>
        {patientTab === "fhir" && !isStickyHeader && (
          <Box
            id="sticky-header"
            bg={useColorModeValue("gray.100", "gray.900")}
            pt={2}
            position={"sticky"}
            top={isImpersonatingOffset}
            zIndex={3}
          >
            <PatientSearch
              onSearch={onUserSearch}
              onClear={onUserClearSearch}
              isLoading={isQueryingDocuments || isQueryingConsolidated}
            />
            <PatientTabs
              filters={filters}
              mappedConsolidated={searchResultsState ?? consolidatedState}
              isIFrame={isEhr}
              isLimited={isLimited}
              headerHeight={isImpersonatingOffset}
            />
          </Box>
        )}
        {patientTab === "fhir" && (
          <>
            <SummaryContent
              mappedConsolidated={searchResultsState ?? consolidatedState}
              isLimited={isLimited}
            />
            <PatientFHIRData
              filters={filters}
              mappedConsolidated={searchResultsState ?? consolidatedState}
              isConsolidatedLoading={
                isQueryingDocuments || isQueryingConsolidated || isSearchingResults
              }
              patientQueryParams={patientQueryParams}
              usingMetriportOnlyBundleFromConsolidated={usingMetriportOnlyBundleFromConsolidated}
              setSidePanelContent={setSidePanelContent}
              sidePanelContent={sidePanelContent}
              isSearchMode={!!searchResultsState}
              usingSectionSearch={usingSectionSearch}
            />
          </>
        )}
        {patientTab === "documents" && (
          <RawDocuments
            searchDocFilters={searchDocFilters}
            documents={documentsState}
            isListingDocuments={isListingDocuments}
            actions={{
              onSearchDocuments: onSearchPatientDocuments,
            }}
            isLimited={isLimited}
          />
        )}
      </Box>
      <DocumentProgressContainer dqProgress={dqProgress} />
      <PatientMatches
        isOpen={patientMatchesDisclosure.isOpen || patientTab === "matches"}
        patient={patient}
        patientMatches={patientMatches}
        actions={{
          onClose: () => {
            setPatientTabAndParams("fhir");
            patientMatchesDisclosure.onClose();
          },
        }}
      />
      <PatientForm
        isOpen={patientFormDisclosure.isOpen}
        selectedPatient={patient}
        facilities={facilities}
        isSubmitting={isSubmitting}
        actions={{
          onCloseForm: patientFormDisclosure.onClose,
          onCreateOrUpdatePatient: async patient => {
            const resp = await createOrUpdatePatient(patient);
            setPatient(resp);
          },
        }}
      />
      <MrModal
        isOpen={patientMRDisclosure.isOpen}
        isDownloading={isMrDownloading}
        existingMrStatus={existingMrStatus}
        actions={{
          onCloseModal: patientMRDisclosure.onClose,
          onDownloadMR,
        }}
      />
      <DemographicsModal
        isOpen={patientDemosDisclosure.isOpen}
        patient={patient}
        lastDocQuery={lastDocQueryState}
        actions={{
          onCloseModal: patientDemosDisclosure.onClose,
        }}
      />
    </>
  );
}
